import {Component} from '@theme/component';

const WATCHED_COOKIE_NAME = 'cwv_watched_episodes';
const WATCHED_COOKIE_MAX_AGE = 60 * 60 * 24 * 365;

/** @returns {Set<string>} */
function getWatchedVideoIds() {
    const match = document.cookie.match(new RegExp(`(?:^|;\\s*)${WATCHED_COOKIE_NAME}=([^;]*)`));
    if (!match) return new Set();

    try {
        return new Set(JSON.parse(decodeURIComponent(match[1])));
    } catch {
        return new Set();
    }
}

/** @param {string} videoId */
function markVideoAsWatched(videoId) {
    const watched = getWatchedVideoIds();
    watched.add(videoId);
    document.cookie = `${WATCHED_COOKIE_NAME}=${encodeURIComponent(JSON.stringify([...watched]))}; max-age=${WATCHED_COOKIE_MAX_AGE}; path=/`;
}

/** @param {number} seconds */
function formatTime(seconds) {
    const total = Math.max(0, Math.floor(seconds || 0));
    const hrs = Math.floor(total / 3600);
    const mins = Math.floor((total % 3600) / 60);
    const secs = total % 60;
    const paddedSecs = String(secs).padStart(2, '0');

    return hrs > 0 ? `${hrs}:${String(mins).padStart(2, '0')}:${paddedSecs}` : `${mins}:${paddedSecs}`;
}

/**
 * Normalizes a category identifier for comparison — case-insensitive, treating underscores,
 * hyphens, and whitespace as equivalent separators (e.g. "weight_loss" matches "Weight Loss").
 * @param {string} value
 */
function normalizeCategoryKey(value) {
    return value.trim().toLowerCase().replace(/[\s_-]+/g, ' ');
}

const ICON_MARKUP = {
    play: '<path d="M0 0.6v10.8a0.6 0.6 0 0 0 0.92.5l8.4-5.4a0.6 0.6 0 0 0 0-1l-8.4-5.4A0.6 0.6 0 0 0 0 0.6Z" fill="white"/>',
    pause: '<rect x="0" y="0" width="3" height="12" fill="white"/><rect x="7" y="0" width="3" height="12" fill="white"/>',
    volume:
        '<path d="M1 5H4L8 1.5V12.5L4 9H1V5Z" fill="white"/>' +
        '<path d="M11 4.3C12.3 5.4 12.3 8.6 11 9.7" stroke="white" stroke-width="1.2" stroke-linecap="round" fill="none"/>' +
        '<path d="M13 2.3C15.7 4.6 15.7 9.4 13 11.7" stroke="white" stroke-width="1.2" stroke-linecap="round" fill="none"/>',
    muted:
        '<path d="M1 5H4L8 1.5V12.5L4 9H1V5Z" fill="white"/>' +
        '<path d="M11 4.5L16 9.5M16 4.5L11 9.5" stroke="white" stroke-width="1.2" stroke-linecap="round"/>',
};

/** @param {boolean} isWatched */
function watchedBadgeMarkup(isWatched) {
    return isWatched
        ? `<svg class="tw:shrink-0 tw:ml-1" width="17" height="17" viewBox="0 0 17 17" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
            <path d="M1.11989 5.30195C2.28914 2.49876 5.05236 0.530029 8.28027 0.530029C12.5575 0.530029 16.0303 3.99779 16.0303 8.28003C16.0303 12.5623 12.5625 16.03 8.28027 16.03C3.99804 16.03 0.530273 12.5573 0.530273 8.28003" stroke="#CFCFCF" stroke-width="1.06" stroke-miterlimit="10"/>
            <path d="M4.25027 5.30398H0.530273V1.58398" stroke="#CFCFCF" stroke-width="1.06" stroke-miterlimit="10"/>
            <path d="M6.73047 11.3777V5.17765L11.6905 8.28003L6.73047 11.3777Z" fill="#CFCFCF"/>
          </svg>`
        : `<svg class="tw:shrink-0 tw:ml-1" width="8" height="11" viewBox="0 0 8 11" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
            <path d="M0 0V11L8 5.5L0 0Z" fill="#CDD6E0"/>
          </svg>`;
}

/**
 * @typedef {object} Slide
 * @property {string} title
 * @property {string} color
 * @property {string} subtitle_color
 * @property {string} [image] - Hero/thumbnail image for this category
 * @property {string} [description] - Hero description shown on desktop
 * @property {Video[]} videos
 *
 * @typedef {object} ShareLinks
 * @property {string} [instagram] - Instagram Story link for this video
 * @property {string} [tiktok] - TikTok video link
 *
 * @typedef {object} Video
 * @property {string} url - Shopify-hosted video file URL
 * @property {string} title
 * @property {string} duration
 * @property {string} preview_image - Shopify-generated thumbnail for this video
 * @property {ShareLinks} [share_links]
 *
 * @typedef {object} Refs
 * @property {HTMLElement} swiper
 * @property {HTMLElement} content
 * @property {HTMLParagraphElement} slideTitle
 * @property {HTMLParagraphElement} slideSubtitle
 * @property {HTMLParagraphElement} slideCount
 * @property {HTMLUListElement} videoList
 * @property {HTMLElement} playLabel
 * @property {HTMLElement} desktopContent
 * @property {HTMLUListElement} desktopSidebar
 * @property {HTMLParagraphElement} desktopHeroTitle
 * @property {HTMLParagraphElement} desktopHeroDescription
 * @property {HTMLElement} desktopHeroAccent
 * @property {HTMLParagraphElement} desktopHeroLabel
 * @property {HTMLImageElement} desktopHeroImage
 * @property {HTMLButtonElement} desktopPlayButton
 * @property {HTMLElement} desktopPlayLabel
 * @property {HTMLParagraphElement} desktopSlideCount
 * @property {HTMLElement} desktopEpisodesCount
 * @property {HTMLUListElement} desktopGrid
 * @property {HTMLDialogElement} dialog
 * @property {HTMLVideoElement} videoFrame
 * @property {HTMLButtonElement} closeBtn
 * @property {HTMLElement} currentTimeLabel
 * @property {HTMLElement} remainingTimeLabel
 * @property {HTMLInputElement} progressBar
 * @property {HTMLButtonElement} skipBackBtn
 * @property {HTMLButtonElement} playPauseBtn
 * @property {SVGElement} playPauseIcon
 * @property {HTMLButtonElement} skipForwardBtn
 * @property {HTMLButtonElement} muteBtn
 * @property {SVGElement} volumeIcon
 * @property {HTMLButtonElement} shareBtn
 * @property {HTMLDialogElement} shareDialog
 * @property {HTMLButtonElement} closeShareBtn
 * @property {HTMLButtonElement} closeShareOptionsBtn
 * @property {HTMLAnchorElement} shareInstagramLink
 * @property {HTMLAnchorElement} shareTiktokLink
 * @property {HTMLAnchorElement} shareWhatsappLink
 *
 * @extends Component<Refs>
 */
class ColorWallVideos extends Component {
    requiredRefs = [
        'swiper',
        'content',
        'slideTitle',
        'slideSubtitle',
        'slideCount',
        'videoList',
        'playLabel',
        'desktopContent',
        'desktopMain',
        'desktopSidebar',
        'desktopHeroTitle',
        'desktopHeroDescription',
        'desktopHeroAccent',
        'desktopHeroLabel',
        'desktopHeroImage',
        'desktopPlayButton',
        'desktopPlayLabel',
        'desktopSlideCount',
        'desktopEpisodesCount',
        'desktopGrid',
        'dialog',
        'videoFrame',
        'closeBtn',
        'currentTimeLabel',
        'remainingTimeLabel',
        'progressBar',
        'skipBackBtn',
        'playPauseBtn',
        'playPauseIcon',
        'skipForwardBtn',
        'muteBtn',
        'volumeIcon',
        'shareBtn',
        'shareDialog',
        'closeShareBtn',
        'closeShareOptionsBtn',
        'shareInstagramLink',
        'shareTiktokLink',
        'shareWhatsappLink',
    ];

    /** Fraction of a video that counts as "watched" */
    static #WATCHED_THRESHOLD = 0.9;

    /** Seconds the skip buttons jump */
    static #SKIP_SECONDS = 10;

    /** Delay (ms) added per item when the episode/video list animates in, so items cascade in one after another */
    static #ITEM_STAGGER_MS = 40;

    /** @type {Slide[]} */
    #slides = [];

    /** @type {import('swiper').Swiper | null} */
    #swiper = null;

    /** @type {ReturnType<typeof setTimeout> | null} */
    #fadeTimer = null;

    /** @type {Video | null} */
    #currentVideo = null;

    #handleTimeUpdate = () => {
        const {currentTime, duration} = this.refs.videoFrame;

        this.refs.currentTimeLabel.textContent = formatTime(currentTime);
        this.refs.remainingTimeLabel.textContent = duration > 0 ? `-${formatTime(duration - currentTime)}` : '-0:00';

        const percent = duration > 0 ? (currentTime / duration) * 100 : 0;
        this.refs.progressBar.value = String(percent);
        this.refs.progressBar.style.background = `linear-gradient(to right, #fff ${percent}%, rgba(255, 255, 255, 0.3) ${percent}%)`;

        if (duration > 0 && currentTime / duration >= ColorWallVideos.#WATCHED_THRESHOLD) {
            this.#markCurrentVideoWatched();
        }
    };

    #handlePlayStateChange = () => {
        this.refs.playPauseIcon.innerHTML = this.refs.videoFrame.paused ? ICON_MARKUP.play : ICON_MARKUP.pause;
    };

    #handleVolumeChange = () => {
        const muted = this.refs.videoFrame.muted || this.refs.videoFrame.volume === 0;
        this.refs.volumeIcon.innerHTML = muted ? ICON_MARKUP.muted : ICON_MARKUP.volume;
    };

    connectedCallback() {
        super.connectedCallback();

        const dataEl = this.querySelector('script[type="application/json"]');
        this.#slides = dataEl ? JSON.parse(dataEl.textContent ?? '[]') : [];

        const initialIndex = this.#getInitialSlideIndex();
        this.#initSwiper(initialIndex);
        this.#renderVideos(initialIndex, false);

        this.refs.closeBtn.addEventListener('click', () => this.#closeVideo());

        this.#bindVideoIndexClicks(this.refs.videoList);
        this.#bindVideoIndexClicks(this.refs.desktopGrid);

        this.refs.desktopSidebar.addEventListener('click', (e) => {
            const btn = /** @type {HTMLElement | null} */ (e.target?.closest('[data-slide-index]'));
            if (!btn) return;

            this.#swiper?.slideTo(Number(btn.dataset.slideIndex));
        });

        this.refs.dialog.addEventListener('click', (e) => {
            if (e.target === this.refs.dialog) this.#closeVideo();
        });

        this.refs.videoFrame.addEventListener('timeupdate', this.#handleTimeUpdate);
        this.refs.videoFrame.addEventListener('ended', () => this.#markCurrentVideoWatched());
        this.refs.videoFrame.addEventListener('play', this.#handlePlayStateChange);
        this.refs.videoFrame.addEventListener('pause', this.#handlePlayStateChange);
        this.refs.videoFrame.addEventListener('volumechange', this.#handleVolumeChange);

        this.refs.shareDialog.addEventListener('click', (e) => {
            if (e.target === this.refs.shareDialog) this.closeShareOptions();
        });
    }

    /** @returns {Slide | undefined} */
    #currentSlide() {
        return this.#slides[this.#swiper?.activeIndex ?? 0];
    }

    /**
     * Delegates clicks on `[data-video-index]` buttons within a list to opening that episode
     * of the currently active slide. Shared by the mobile episode list and the desktop grid.
     * @param {HTMLElement} listEl
     */
    #bindVideoIndexClicks(listEl) {
        listEl.addEventListener('click', (e) => {
            const btn = /** @type {HTMLElement | null} */ (e.target?.closest('[data-video-index]'));
            if (!btn) return;

            const video = this.#currentSlide()?.videos[Number(btn.dataset.videoIndex)];
            if (video) this.#openVideo(video);
        });
    }

    /**
     * Opens the episode indicated by the play button's own label (e.g. "Play EP. 3" → episode 3),
     * so the button always opens whatever it currently says it will.
     */
    playCurrentEpisode() {
        const slide = this.#currentSlide();
        if (!slide) return;

        const episodeNumber = Number(this.refs.playLabel.textContent?.match(/(\d+)\s*$/)?.[1] ?? 1);
        const video = slide.videos[episodeNumber - 1];
        if (video) this.#openVideo(video);
    }

    togglePlayPause() {
        if (this.refs.videoFrame.paused) {
            this.refs.videoFrame.play().catch(() => {
            });
        } else {
            this.refs.videoFrame.pause();
        }
    }

    skipForward() {
        const {currentTime, duration} = this.refs.videoFrame;
        this.refs.videoFrame.currentTime = Math.min(duration || currentTime, currentTime + ColorWallVideos.#SKIP_SECONDS);
    }

    skipBackward() {
        const {currentTime} = this.refs.videoFrame;
        this.refs.videoFrame.currentTime = Math.max(0, currentTime - ColorWallVideos.#SKIP_SECONDS);
    }

    toggleMute() {
        this.refs.videoFrame.muted = !this.refs.videoFrame.muted;
    }

    openShareOptions() {
        const links = this.#currentVideo?.share_links ?? {};

        this.#setShareLink(this.refs.shareInstagramLink, links.instagram);
        this.#setShareLink(this.refs.shareTiktokLink, links.tiktok);
        // WhatsApp doesn't have its own asset to share — it forwards the Instagram Story link as message text
        this.#setShareLink(
            this.refs.shareWhatsappLink,
            links.instagram ? `https://wa.me/?text=${encodeURIComponent(links.instagram)}` : undefined
        );

        this.refs.shareDialog.showModal();
    }

    closeShareOptions() {
        this.refs.shareDialog.close();
    }

    /**
     * @param {HTMLAnchorElement} link
     * @param {string | undefined} url
     */
    #setShareLink(link, url) {
        link.classList.toggle('tw:hidden', !url);
        link.href = url || '#';
    }

    /** @param {Event} event */
    seek(event) {
        const input = /** @type {HTMLInputElement} */ (event.target);
        const duration = this.refs.videoFrame.duration;
        if (duration > 0) {
            this.refs.videoFrame.currentTime = (Number(input.value) / 100) * duration;
        }
    }

    #markCurrentVideoWatched() {
        const url = this.#currentVideo?.url;
        if (!url || getWatchedVideoIds().has(url)) return;

        markVideoAsWatched(url);

        const slide = this.#currentSlide();
        if (slide) this.#applySlide(slide);
    }

    /**
     * @param {Slide} slide
     * @param {Set<string>} watched
     */
    #nextEpisodeNumber(slide, watched) {
        const nextIndex = slide.videos.findIndex((v) => !watched.has(v.url));
        return (nextIndex === -1 ? slide.videos.length - 1 : nextIndex) + 1;
    }

    /**
     * Finds the slide matching the `?category=` URL param (e.g. `weight_loss` → "Weight Loss"),
     * defaulting to the first slide when the param is absent or doesn't match any category.
     * @returns {number}
     */
    #getInitialSlideIndex() {
        const category = new URLSearchParams(window.location.search).get('category');
        if (!category) return 0;

        const key = normalizeCategoryKey(category);
        const index = this.#slides.findIndex((slide) => normalizeCategoryKey(slide.title) === key);
        return index === -1 ? 0 : index;
    }

    /** @param {number} initialSlide */
    #initSwiper(initialSlide) {
        this.#swiper = new Swiper(this.refs.swiper, {
            slidesPerView: 1.05,
            spaceBetween: 10,
            initialSlide,
            // Otherwise Swiper fires `slideChange` synchronously during construction — before
            // `this.#swiper` is even assigned — which reads activeIndex as 0 and races with the
            // explicit #renderVideos(initialSlide, false) call right after this constructor returns.
            runCallbacksOnInit: false,
            pagination: {
                el: '.swiper-pagination',
                clickable: true,
            },
            on: {
                slideChange: () => {
                    this.#renderVideos(this.#swiper?.activeIndex ?? 0, true);
                },
            },
        });
    }

    /**
     * @param {number} index
     * @param {boolean} animate
     */
    #renderVideos(index, animate) {
        const slide = this.#slides[index];
        if (!slide) return;

        if (!animate) {
            this.#applySlide(slide);
            return;
        }

        this.style.setProperty('--cwv-accent', slide.color ?? '#fff');
        // Sidebar active-state highlight tracks the swipe/click immediately; only the content panels fade
        this.#renderDesktopSidebar(index);

        // Cancel any in-flight fade before starting a new one
        if (this.#fadeTimer !== null) {
            clearTimeout(this.#fadeTimer);
            this.#fadeTimer = null;
            this.refs.content.classList.remove('cwv-fading');
            this.refs.desktopMain.classList.remove('cwv-fading');
        }

        this.refs.content.classList.add('cwv-fading');
        this.refs.desktopMain.classList.add('cwv-fading');

        this.#fadeTimer = setTimeout(() => {
            this.#applyMobileSlide(slide);
            this.#applyDesktopContent(slide);
            this.refs.content.classList.remove('cwv-fading');
            this.refs.desktopMain.classList.remove('cwv-fading');
            this.#fadeTimer = null;
        }, 260);
    }

    /** @param {Slide} slide */
    #applySlide(slide) {
        this.style.setProperty('--cwv-accent', slide.color ?? '#fff');
        this.#applyMobileSlide(slide);
        this.#applyDesktopSlide(slide);
    }

    /** @param {Slide} slide */
    #applyMobileSlide(slide) {
        const watched = getWatchedVideoIds();

        this.refs.slideTitle.textContent = slide.title;
        this.refs.slideSubtitle.style.color = slide.subtitle_color ?? '';
        this.refs.slideCount.textContent = `${slide.videos.length} episodes`;
        this.refs.videoList.innerHTML = slide.videos
            .map(
                (v, i) => `
          <li class="cwv-item-in tw:border-t tw:border-white/15 tw:mx-4" style="animation-delay: ${i * ColorWallVideos.#ITEM_STAGGER_MS}ms">
            <button type="button" data-video-index="${i}" class="tw:w-full tw:flex tw:items-center tw:gap-3  tw:py-3.5 tw:text-left tw:bg-transparent tw:border-0 tw:cursor-pointer tw:appearance-none tw:text-white">
              <span class="tw:shrink-0 tw:w-[22px] tw:text-[16px] tw:font-medium tw:tracking-[0.03em] tw:leading-none tw:self-center" style="color: var(--cwv-accent, #fff)">${String(i + 1).padStart(2, '0')}</span>
              <div class="tw:flex-1 tw:min-w-0">
                <p class="tw:m-0 tw:text-[11px] tw:uppercase tw:tracking-[0.05em] tw:leading-[1.4] tw:font-medium tw:truncate">${v.title}</p>

              </div>
              <div class="tw:flex tw:justify-end tw:items-center tw:w-15 tw:gap-x-1.5">
               <p class="tw:m-0 tw:mt-1 tw:text-[11px] tw:text-white/50">${v.duration}</p>
              ${watchedBadgeMarkup(watched.has(v.url))}
              </div>
            </button>
          </li>`
            )
            .join('');

        this.refs.playLabel.textContent = `Play EP. ${this.#nextEpisodeNumber(slide, watched)}`;
    }

    /** @param {Slide} slide */
    #applyDesktopSlide(slide) {
        this.#renderDesktopSidebar(this.#slides.indexOf(slide));
        this.#applyDesktopContent(slide);
    }

    /** @param {Slide} slide */
    #applyDesktopContent(slide) {
        const watched = getWatchedVideoIds();

        this.#renderDesktopHero(slide);
        this.#renderDesktopGrid(slide, watched);
        this.refs.desktopPlayLabel.textContent = `Play EP. ${this.#nextEpisodeNumber(slide, watched)}`;
        this.refs.desktopSlideCount.textContent = `${slide.videos.length} episodes`;
    }

    /** @param {number} activeIndex */
    #renderDesktopSidebar(activeIndex) {
        this.refs.desktopSidebar.innerHTML = this.#slides
            .map(
                (s, i) => `
          <li>
  <button
    type="button"
    data-slide-index="${i}"
    class="tw:block tw:w-full tw:text-left tw:bg-transparent tw:border-0 tw:p-0 tw:pl-4 tw:cursor-pointer tw:text-[16px] tw:tracking-[0.08em] tw:transition-colors tw:duration-200"
    style="
      color: ${i === activeIndex ? '#fff' : '#8A8A8F'};
      border-left: 2px solid ${i === activeIndex ? s.color : 'none'};
    "
  >
    ${s.title}
  </button>
</li>`
            )
            .join('');
    }

    /** @param {Slide} slide */
    #renderDesktopHero(slide) {
        this.refs.desktopHeroTitle.textContent = slide.title;
        this.refs.desktopHeroDescription.textContent = slide.description ?? '';
        this.refs.desktopHeroAccent.style.borderTopColor = slide.color ?? '#fff';
        this.refs.desktopHeroLabel.style.color = slide.color ?? '#fff';
        this.refs.desktopHeroImage.src = slide.image ?? '';
        this.refs.desktopPlayButton.style.backgroundColor = slide.color ?? '#fff';
    }

    /**
     * @param {Slide} slide
     * @param {Set<string>} watched
     */
    #renderDesktopGrid(slide, watched) {
        this.refs.desktopEpisodesCount.textContent = `(${slide.videos.length})`;
        this.refs.desktopGrid.innerHTML = slide.videos
            .map(
                (v, i) => `
          <li class="cwv-item-in" style="animation-delay: ${i * ColorWallVideos.#ITEM_STAGGER_MS}ms">
            <button type="button" data-video-index="${i}"
                    class="tw:block tw:w-full tw:text-left tw:bg-transparent tw:border-0 tw:p-0 tw:cursor-pointer tw:appearance-none tw:text-white">
              <div class="tw:relative tw:w-full tw:aspect-[3/4] tw:overflow-hidden tw:rounded-[8px]">
                <img src="${v.preview_image}" alt="" loading="lazy" class="tw:w-full tw:h-full tw:object-cover tw:block">
              </div>
             <p class="tw:mt-2.5 tw:text-[16px] tw:tracking-[0.8px] tw:line-clamp-2 tw:text-white">${v.title}</p>
            <p class="tw:text-[12px]  tw:tracking-[0.6px] tw:text-[#F4F4F2]">Ep. ${String(i + 1).padStart(2, '0')} &middot; <span class="tw:text-[#8A8A8F]">${v.duration}</span> </p>
            </button>
          </li>`
            )
            .join('');
    }

    /** @param {Video} video */
    #openVideo(video) {
        this.#currentVideo = video;

        this.refs.currentTimeLabel.textContent = '0:00';
        this.refs.remainingTimeLabel.textContent = '-0:00';
        this.refs.progressBar.value = '0';
        this.refs.progressBar.style.background = '';

        this.refs.videoFrame.muted = false;
        this.refs.videoFrame.src = video.url;
        this.refs.dialog.showModal();
        this.refs.videoFrame.play().catch(() => {
        });
        document.documentElement.setAttribute('scroll-lock', '');
    }

    #closeVideo() {
        if (this.refs.shareDialog.open) this.refs.shareDialog.close();

        this.refs.videoFrame.pause();
        this.refs.videoFrame.removeAttribute('src');
        this.refs.videoFrame.load();
        this.refs.dialog.close();
        document.documentElement.removeAttribute('scroll-lock');

        const slide = this.#currentSlide();
        if (slide) this.#applySlide(slide);
    }
}

if (!customElements.get('color-wall-videos')) {
    customElements.define('color-wall-videos', ColorWallVideos);
}
